import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, X, RefreshCw, Calendar, Hash } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Define markdown components outside the component to avoid recreation on each render
const markdownComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-purple-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-stone-800 mt-4 mb-2 flex items-center gap-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-stone-700 mt-3 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-stone-600 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-3 text-stone-600">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-3 text-stone-600">{children}</ol>,
  li: ({ children }) => <li className="text-stone-600">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-stone-800">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-purple-300 pl-4 italic text-stone-500 my-3">
      {children}
    </blockquote>
  ),
};

const AIProgressReport = ({ open, onClose, hive, token }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    if (!hive || !token) return;
    
    setLoading(true);
    setError(null);
    setReport(null);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/hives/${hive.id}/ai-progress-report`, { headers });
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate progress report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [hive, token]);

  useEffect(() => {
    if (open && hive) {
      fetchReport();
    }
  }, [open, hive, fetchReport]);

  const handleClose = useCallback(() => {
    setReport(null);
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            AI Progress Report
          </DialogTitle>
        </DialogHeader>
        
        {/* Hive Info Header */}
        {hive && (
          <div className="flex-shrink-0 bg-purple-50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-purple-900">
                  Hive {hive.hive_number}
                  {hive.nickname && <span className="text-purple-600 ml-2">&quot;{hive.nickname}&quot;</span>}
                </h3>
                {hive.yard && <p className="text-sm text-purple-700">{hive.yard}</p>}
              </div>
              {report && (
                <div className="text-right text-sm text-purple-700">
                  <div className="flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {report.inspection_count} inspections
                  </div>
                  {report.last_inspection && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Last: {report.last_inspection}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Sparkles className="h-12 w-12 text-purple-400 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                </div>
              </div>
              <p className="text-stone-600 mt-4 text-center">
                Analyzing inspection history...
                <br />
                <span className="text-sm text-stone-400">This may take a moment</span>
              </p>
            </div>
          )}
          
          {error && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">Error</h3>
              <p className="text-sm text-stone-600 mb-4">{error}</p>
              <Button onClick={fetchReport} className="bg-purple-600 hover:bg-purple-700 text-white">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          )}
          
          {report && !loading && (
            <div className="prose prose-stone prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>
                {report.report}
              </ReactMarkdown>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 pt-4 border-t border-stone-200 flex gap-3">
          {report && !loading && (
            <Button
              variant="secondary"
              onClick={fetchReport}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate
            </Button>
          )}
          <Button
            variant={report ? "default" : "secondary"}
            onClick={handleClose}
            className={report ? "flex-1 bg-purple-600 hover:bg-purple-700 text-white" : "flex-1"}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIProgressReport;
